/// <reference lib="dom" />

/* eslint-disable unicorn/no-document-cookie -- consent persistence is the module's purpose */

/**
 * Lenus consent engine — replaces vendored orestbida/cookieconsent runtime.
 * Cookie persistence format is compatible with CookieConsent v3 `cc_cookie`.
 *
 * @see https://github.com/orestbida/cookieconsent (MIT)
 */

import type {
  CookieConsentConfig,
  CookieConsentCookie,
  CookieConsentInstance,
  CookieCategories,
} from './types.ts'

export type StoredConsentCookie = {
  categories: string[]
  revision: string | number
  consentTimestamp: string
  consentId: string
  lastConsentTimestamp: string
  expirationTime: number
  data: unknown
  services: Record<string, string[]>
  languageCode: string
}

const DEFAULT_COOKIE_PATH = '/'
const MS_PER_DAY = 86_400_000

function createConsentId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, char => {
    const random = Math.floor(Math.random() * 16)
    const value = char === 'x' ? random : (random & 0x3) | 0x8
    return value.toString(16)
  })
}

function parseCookieValue(rawValue: string): StoredConsentCookie | null {
  try {
    const parsed = JSON.parse(
      decodeURIComponent(rawValue),
    ) as StoredConsentCookie

    if (!parsed || typeof parsed !== 'object') {
      return null
    }

    if (!Array.isArray(parsed.categories)) {
      return null
    }

    if (typeof parsed.consentId !== 'string' || !parsed.consentId) {
      return null
    }

    return parsed
  } catch {
    return null
  }
}

function readStoredCookie(cookieName: string): StoredConsentCookie | null {
  const match = document.cookie.match(
    new RegExp(
      `(?:^|;\\s*)${cookieName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*=\\s*([^;]+)`,
    ),
  )

  if (!match?.[1]) {
    return null
  }

  return parseCookieValue(match[1])
}

function writeStoredCookie({
  cookieName,
  value,
  expiresAfterDays,
  path,
  domain,
  sameSite,
  secure,
}: {
  cookieName: string
  value: StoredConsentCookie
  expiresAfterDays: number
  path: string
  domain?: string
  sameSite: 'Lax' | 'Strict' | 'None'
  secure?: boolean
}): void {
  const expiresAfterMs = expiresAfterDays * MS_PER_DAY
  const expirationDate = new Date(Date.now() + expiresAfterMs)
  value.expirationTime = expirationDate.getTime()

  const encodedValue = encodeURIComponent(JSON.stringify(value))
  let cookieStr = `${cookieName}=${encodedValue}; expires=${expirationDate.toUTCString()}; Path=${path}; SameSite=${sameSite}`

  const { hostname, protocol } = location

  if (domain && hostname.includes('.')) {
    cookieStr += `; Domain=${domain}`
  }

  if (secure && protocol === 'https:') {
    cookieStr += '; Secure'
  }

  document.cookie = cookieStr
}

function arrayDiff(next: string[], previous: string[]): string[] {
  const previousSet = new Set(previous)
  const nextSet = new Set(next)

  return [
    ...next.filter(category => !previousSet.has(category)),
    ...previous.filter(category => !nextSet.has(category)),
  ]
}

function getReadOnlyCategories(categories: CookieCategories): string[] {
  return Object.keys(categories).filter(
    category => categories[category]?.readOnly,
  )
}

function getAllCategoryNames(categories: CookieCategories): string[] {
  return Object.keys(categories)
}

function resolveAcceptedCategories({
  input,
  categories,
}: {
  input: string | string[]
  categories: CookieCategories
}): string[] {
  const readOnly = getReadOnlyCategories(categories)
  const allCategories = getAllCategoryNames(categories)

  if (input === 'all') {
    return allCategories
  }

  const requested = Array.isArray(input) ? input : [input]
  const normalized = requested.filter(category =>
    allCategories.includes(category),
  )

  return [...new Set([...readOnly, ...normalized])]
}

function isLegacyRevision(revision: unknown): boolean {
  if (typeof revision === 'number') {
    return true
  }

  return typeof revision === 'string' && /^\d+$/.test(revision)
}

function parseDateRevision(revision: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(revision)) {
    return null
  }

  const timestamp = new Date(`${revision}T00:00:00.000Z`).getTime()

  return Number.isNaN(timestamp) ? null : timestamp
}

function isRevisionCompatible({
  storedRevision,
  currentRevision,
  consentTimestamp,
}: {
  storedRevision: unknown
  currentRevision: string
  consentTimestamp?: string
}): boolean {
  if (String(storedRevision) === currentRevision) {
    return true
  }

  if (!isLegacyRevision(storedRevision)) {
    return false
  }

  const currentRevisionTimestamp = parseDateRevision(currentRevision)
  const storedConsentTimestamp = consentTimestamp
    ? new Date(consentTimestamp).getTime()
    : Number.NaN

  if (
    currentRevisionTimestamp === null ||
    Number.isNaN(storedConsentTimestamp)
  ) {
    return false
  }

  // CookieConsent v3 stored numeric revisions (e.g. 0). Accept them only when
  // the original consent happened after the current date-based policy version.
  return storedConsentTimestamp >= currentRevisionTimestamp
}

function isStoredCookieValid({
  stored,
  revision,
  expiresAfterDays,
}: {
  stored: StoredConsentCookie | null
  revision: string
  expiresAfterDays: number
}): boolean {
  if (!stored) {
    return false
  }

  if (
    !isRevisionCompatible({
      storedRevision: stored.revision,
      currentRevision: revision,
      consentTimestamp: stored.consentTimestamp,
    })
  ) {
    return false
  }

  if (!stored.consentTimestamp || !stored.lastConsentTimestamp) {
    return false
  }

  if (stored.expirationTime && Date.now() > stored.expirationTime) {
    return false
  }

  if (
    !stored.expirationTime &&
    stored.consentTimestamp &&
    expiresAfterDays > 0
  ) {
    const consentTime = new Date(stored.consentTimestamp).getTime()
    if (Date.now() - consentTime > expiresAfterDays * MS_PER_DAY) {
      return false
    }
  }

  return true
}

export function createLenusConsentEngine(): CookieConsentInstance {
  let config: CookieConsentConfig | null = null
  let acceptedCategories: string[] = []
  let invalidConsent = true
  let consentId: string | null = null
  let consentTimestamp: Date | null = null
  let lastConsentTimestamp: Date | null = null

  const getCookieOptions = () => {
    const cookieOptions = config?.cookie

    return {
      name: cookieOptions?.name ?? 'cc_cookie',
      expiresAfterDays: cookieOptions?.expiresAfterDays ?? 182,
      path: cookieOptions?.path ?? DEFAULT_COOKIE_PATH,
      domain: cookieOptions?.domain,
      sameSite: cookieOptions?.sameSite ?? 'Lax',
      secure: cookieOptions?.secure ?? true,
      useLocalStorage: cookieOptions?.useLocalStorage ?? false,
    }
  }

  const persistConsent = (): void => {
    if (!config || !consentId || !consentTimestamp || !lastConsentTimestamp) {
      return
    }

    const cookieOptions = getCookieOptions()

    if (cookieOptions.useLocalStorage) {
      localStorage.setItem(
        cookieOptions.name,
        JSON.stringify(buildStoredCookie()),
      )
      return
    }

    writeStoredCookie({
      cookieName: cookieOptions.name,
      value: buildStoredCookie(),
      expiresAfterDays: cookieOptions.expiresAfterDays,
      path: cookieOptions.path,
      domain: cookieOptions.domain,
      sameSite: cookieOptions.sameSite,
      secure: cookieOptions.secure,
    })
  }

  const buildStoredCookie = (): StoredConsentCookie => {
    if (!config || !consentId || !consentTimestamp || !lastConsentTimestamp) {
      throw new Error('Consent engine is not initialized')
    }

    return {
      categories: [...acceptedCategories],
      revision: config.version,
      consentTimestamp: consentTimestamp.toISOString(),
      consentId,
      lastConsentTimestamp: lastConsentTimestamp.toISOString(),
      expirationTime: 0,
      data: null,
      services: {},
      languageCode: config.language.default,
    }
  }

  const loadPersistedConsent = (): void => {
    if (!config) {
      return
    }

    const cookieOptions = getCookieOptions()
    const stored = cookieOptions.useLocalStorage
      ? parseCookieValue(localStorage.getItem(cookieOptions.name) ?? '')
      : readStoredCookie(cookieOptions.name)

    if (
      !isStoredCookieValid({
        stored,
        revision: config.version,
        expiresAfterDays: cookieOptions.expiresAfterDays,
      })
    ) {
      invalidConsent = true
      acceptedCategories = getReadOnlyCategories(config.categories)
      consentId = null
      consentTimestamp = null
      lastConsentTimestamp = null
      return
    }

    invalidConsent = false
    acceptedCategories = resolveAcceptedCategories({
      input: stored?.categories ?? [],
      categories: config.categories,
    })
    consentId = stored?.consentId ?? null
    consentTimestamp = stored?.consentTimestamp
      ? new Date(stored.consentTimestamp)
      : null
    lastConsentTimestamp = stored?.lastConsentTimestamp
      ? new Date(stored.lastConsentTimestamp)
      : null
  }

  const saveConsent = (nextCategories: string[]): void => {
    if (!config) {
      return
    }

    const previousCategories = [...acceptedCategories]
    const categoriesChanged =
      arrayDiff(nextCategories, previousCategories).length > 0
    const isFirstConsent = invalidConsent

    if (!isFirstConsent && !categoriesChanged) {
      return
    }

    const now = new Date()

    if (!consentId) {
      consentId = createConsentId()
    }

    if (!consentTimestamp) {
      consentTimestamp = now
    }

    lastConsentTimestamp = now
    acceptedCategories = nextCategories
    invalidConsent = false
    persistConsent()

    const cookiePayload = { cookie: { categories: [...acceptedCategories] } }

    if (isFirstConsent) {
      config.onFirstConsent?.(cookiePayload)
      return
    }

    if (categoriesChanged) {
      config.onChange?.({
        cookie: cookiePayload.cookie,
        changedCategories: arrayDiff(nextCategories, previousCategories),
      })
    }
  }

  return {
    run: userConfig => {
      config = userConfig
      loadPersistedConsent()
    },
    reloadConsent: () => {
      loadPersistedConsent()
    },
    showPreferences: () => {},
    acceptCategory: input => {
      if (!config) {
        return
      }

      saveConsent(
        resolveAcceptedCategories({
          input,
          categories: config.categories,
        }),
      )
    },
    getCookie: (): CookieConsentCookie => ({
      categories: [...acceptedCategories],
    }),
    validConsent: () => !invalidConsent,
  }
}
